import React, { useState, useEffect } from 'react'
import { Table, DataTable, Button, Card, Icon, Banner } from '@arnsbogroup/react-voicearchive'
import FileList from './components/FileList'
import AiVoiceLineList from './components/AiVoiceLineList'
import { Timestamp, PriorityIcons, Span, Buyouts } from 'components/datatable'
import TaskActionDialog from './components/TaskActionDialog'
import ReportIncidentModal from 'components/ReportIncidentModal'
import FileUpload from './components/FileUpload'
import NiceModal from '@ebay/nice-modal-react'
import { useIntl, FormattedMessage } from 'react-intl'
import messages from './messages'

const SupplierTaskPage = ({ history, taskId }) => {

  const [task, setTask] = useState(null)
  const [taskDetails, setTaskDetails] = useState([])
  const [aiVoiceLines, setAiVoiceLines] = useState([])
  const [retakeTasks, setRetakeTasks] = useState(null)
  const [showFileAdjustmentGuide, setShowFileAdjustmentGuide] = useState(false)
  const { formatMessage } = useIntl()
  const domain = window.location.hostname
  const protocol = window.location.protocol

  useEffect(() => {
    fetchTask()
  }, [taskId])

  useEffect(() => {
    if (task?.taskType?.shortname == 'Ai') {
      fetchAiVoiceLines()
    }
  }, [task]);

  const formatBuyouts = (buyouts) => {
    return buyouts.map((buyout) => ({
      quantity: buyout.quantity,
      quantity_type: buyout.quantity_type.name,
      ordertargetmedia: buyout.ordertargetmedia.name,
      territoryMarket: buyout.territoryMarket.name,
      orderusage: buyout.orderusage.name,
      amount: buyout.cost,
      currency: buyout.currency.iso
    }))
  }

  const fetchTask = () => {
    fetch(`/rest/v2/task/${taskId}?includeRelations=1`, { credentials: 'same-origin' }) 
      .then((response) => response.json())
      .then(({data, success, errors}) => {
        if (!success) {
          toastr.error(errors.join(', '))
          return
        }
        setTask(data)
        if (!!(+data.retakecreated) && data?.retakeTasks && data?.retakeTasks.length > 0 && data?.retakeTasks.find((retakeTask) => retakeTask.supplier == data.supplier)) {
          setRetakeTasks(data?.retakeTasks.filter((retakeTask) => retakeTask.supplier == data.supplier))
        }
        setTaskDetails([
          {
            label: formatMessage(messages.title),
            value: data.title,
            show: true
          },
          {
            label: formatMessage(messages.taskType),
            value: data.taskType.name || 'Unknown',
            show: true
          },
          {
            label: formatMessage(messages.jobDeadline),
            value: <Timestamp>{data.deadline}</Timestamp>,
            show: true
          },
          {
            label: formatMessage(messages.fee),
            value: `${!data.cost ? 0 : data.cost} ${data.currency.iso}`,
            show: !data.buyouts.length
          },
          {
            label: formatMessage(messages.buyoutFee),
            value: <>
              { data.buyouts.some((buyout) => buyout.cost == 0) &&
                <p className='text-red-400' >
                  <FormattedMessage {...messages.buyoutsInstructions} />
                </p>
              }
              <Buyouts
                buyouts={formatBuyouts(data.buyouts)}
                supplierApproval={data.supplierapproval}
                declinedReason={data.declinedreason}
              />
            </>,
            show: data.taskType.shortname == 'AU' && !!data.buyouts.length
          },
          {
            label: formatMessage(messages.wordCount),
            value: !data.wordcount ? 0 : data.wordcount,
            show: true
          },
          {
            label: formatMessage(messages.media),
            value: data.media,
            show: !data?.hasUsageTaskSibling
          },
          {
            label: formatMessage(messages.territoryMarket),
            value: !!data?.territoryMarkets.length ? data?.territoryMarkets.map((territoryMarket) => territoryMarket.name).join(', ') : null,
            show: !data?.hasUsageTaskSibling && !!data?.territoryMarkets.length
          },
          {
            label: formatMessage(messages.period),
            value: data?.usage?.name,
            show: !data?.hasUsageTaskSibling
          },
          {
            label: formatMessage(messages.usageCustomerCancelation),
            value: formatMessage(messages.usageCustomerCancelationValue),
            show: data?.taskType?.shortname == 'USAGE'
          },
          {
            label: formatMessage(messages.usageTerms),
            value: formatMessage(messages.usageTermsValue, {
              a: (chunks) => (
                <a className="text-blue-600 underline" href={`${protocol}//${domain}/docs/Advertising-workflow`} target="_blank">
                  {chunks}
                </a>
              )}),
            show: data?.taskType?.shortname == 'USAGE'
          },
          {
            label: formatMessage(messages.audioFormat),
            value: data.audioFormat,
            show: true
          },
          {
            label: formatMessage(messages.production),
            value: data.editingLevel.map((editing) => (editing.name)).join(' ,'),
            show: data?.taskType?.shortname == 'VO' && !!data.editingLevel.length 
          },

          {
            label: formatMessage(messages.services),
            value: data.services && !!data.services.length ? data.services.join(', ') : formatMessage(messages.none),
            show: true
          },
          {
            label: formatMessage(messages.products),
            value: data.products && !!data.products.length ? data.products.join(', ') : formatMessage(messages.none),
            show: true
          },
          
          {
            label: formatMessage(messages.invoiceRef),
            value: `${data.orderid}-${data.uid}`,
            show: true
          },
          {
            label: formatMessage(messages.supplierId),
            value: data?.supplierObject?.userid,
            show: true
          },
          {
            label: formatMessage(messages.supplier),
            value: `${data?.supplierObject?.firstname} ${data?.supplierObject?.lastname}`,
            show: true
          },
          {
            label: formatMessage(messages.client),
            value: data.endCustomer,
            show: data?.endCustomer
          },
          {
            label: formatMessage(messages.coordinator),
            value: data?.coordinator?.name,
            show: true
          },
          {
            label: formatMessage(messages.phone),
            value: data?.coordinator?.telephone,
            show: true
          },
          {
            label: formatMessage(messages.email),
            value: data?.coordinator?.email,
            show: true
          },
        ])
      })
      .catch((error) => toastr.error(error))
  }

  const fetchAiVoiceLines = () => {
    fetch(`/rest/v2/task/${taskId}/ai-voice-lines`, { credentials: 'same-origin' }) 
      .then((response) => response.json())
      .then(({data, success, errors}) => {
        if (!success) {
          toastr.error(errors.join(', '))
          return
        }
        setAiVoiceLines(data)
      })
      .catch((error) => toastr.error(error))
  }

  if (!task) {
    return null
  }


  const approveDenyTask = (action = null) => {
    NiceModal.show(TaskActionDialog, {task, action} )
      .then(fetchTask)
      .catch((error) => {
        toastr.error(error)
      })
  }
  
  const registerIncident = () => {
    NiceModal.show(ReportIncidentModal, { task } )
      .then(fetchTask)
      .catch((error) => {
        toastr.error(error)
      })
  }

  const hideTask = (hidden) => {
    fetch(`/rest/v2/task/${task.uid}/hide`, { 
      method: 'PUT',
      body: JSON.stringify({
        hide: hidden
      }) 
    })
    .then((response) => response.json())
    .then((response) => {
      if (!response.success) {
        toastr.error(response.errors.join(', '))
      } else {
        setTask({ ...task, hidden: hidden })
        toastr.success('success')
      }
    })
    .catch((error) => toastr.error(error))
  }

  const versionFilesTitle = {
    1: formatMessage(messages.versioned),
    2: formatMessage(messages.translated),
    3: formatMessage(messages.voiceOver),
    4: formatMessage(messages.translated),
  }[task.type] ?? ''

  return (
    <div className="flex flex-wrap w-full h-full" >
      <div className="flex-initial h-full overflow-y-scroll w-1/4" >
        {taskDetails.filter(({ show }) => show).map((taskDetail) => (
          <div className='border-b hover:bg-primary-lighter p-2'>
            <p className='font-bold'>
              {taskDetail.label}:
            </p>
            <p>
              {taskDetail.value}
            </p>
          </div>
        ))}
      </div>
      <div className="flex-initial overflow-auto h-full w-3/4" >
        <div className='p-2 space-x-2'>
          { task.supplierPriorities.some((priority) => priority.priority == 10) &&
            <Button size="xs" variant="outline" state="negative"
              onClick={() => registerIncident(task)}
            >
              <FormattedMessage {...messages.registerIncident} />
            </Button>
          }
          { !+task.supplierapproval && task.declinedreason == '' && 
            <>
              <Button size="xs"
                onClick={() => approveDenyTask('approve')}
              >
                <FormattedMessage {...messages.approveTask} />
              </Button>
              <Button size="xs" state="negative"
                onClick={() => approveDenyTask('deny')}
              >
                <FormattedMessage {...messages.denyTask} />
              </Button>
            </>
          }
          { (!!(+task.supplierapproval) || task.declinedreason != '') ? (
            <>
              <Button size="xs" variant="outline"
                onClick={() => approveDenyTask('note')}
              >
                <FormattedMessage {...messages.sendMessage} />
              </Button>
              { task.type == 3 && !!task.taskFiles.length &&
                <Button size="xs" variant="outline"
                  onClick={() => hideTask(+task.hidden ? false : true)}
                >
                  {+task.hidden ? 'Show task' : 'Hide task'}
                </Button>
              }
            </>
          ) : (
              <Button size="xs" variant="outline"
                onClick={() => approveDenyTask('note')}
              >
                <FormattedMessage {...messages.sendMessageWithoutApproving} />
              </Button>
            ) 
          }
        </div>
        <div className="flex h-full overflow-auto" >
          <div className="flex-initial w-1/2 h-full overflow-y-scroll space-y-4 p-2" >
            <Card className="p-2">
              <p className='font-bold' >
                <FormattedMessage {...messages.status} />
              </p>
              <p className="text-negative">
                { task.statusObject.supplierstatus || 'Unknown' }
              </p>
            </Card>
            { task.statusObject.suppliermsg != '' && 
              <Card className="p-2">
                <p className='font-bold' >
                  <FormattedMessage {...messages.details} />
                </p>
                <p>
                  { task.statusObject.suppliermsg }
                </p>
              </Card>
            }
            { task.retakeReason && 
              <Card className="p-2">
                <p className='font-bold' >
                  <FormattedMessage {...messages.retake} />
                  ({ task.retakeReason.supplierName })
                </p>
                <p className='text-negative'>
                  {task.retakeReason.retakeReasonNote}
                </p>
              </Card>
            }
            { (task.externalnote != '' || !!task.communication.length) &&
              <Card className="p-2 space-y-4">
                {!!task.communication.length &&
                  <p className='font-bold' >
                    <FormattedMessage {...messages.messageList} />
                  </p>
                }
                { !!task.communication.length && [...task.communication].reverse().map((message) => (
                  <div className="max-w-md bg-primary text-white rounded-xl p-3 mb-3 self-start relative">
                    <small>
                      <Timestamp>{message.crdate}</Timestamp>
                    </small>
                    <p dangerouslySetInnerHTML={{ __html: message.msg.replace(`Task link: https://system.voicearchive.com/task/${task.uid}`, '').replace('<br><br>', '')}} className="space-y-2"/>
                  </div>
                ))
                }
                <p className='font-bold' >
                  <FormattedMessage {...messages.coordinatorMessage} />
                </p>
                { task.externalnote != '' &&
                  <p className='whitespace-pre-wrap'>
                    {task.externalnote}
                  </p>
                }
                { ["PR", "NPR"].includes(task?.taskType?.shortname) && task.versionTasks
                  .filter(({externalnote}) => externalnote && externalnote != '')
                  .map(({externalnote, uid}) => (
                    <>
                      <p className='font-bold' >
                        <FormattedMessage {...messages.voBrief} values={{
                          taskId: uid
                        }} />
                      </p>
                      <p className='whitespace-pre-wrap'>
                        {externalnote}
                      </p>
                    </>
                  ))
                }
              </Card>
            }
          </div>
          <div className="flex-initial w-1/2 h-full overflow-scroll space-y-4 mt-2" >
            { !!task.jobFiles.length &&
              <div className="h-auto" >
                <div className="text-md px-4 py-2  bg-gray-100 border-b border-gray-200 font-bold flex justify-between items-center">
                    <div>
                      <FormattedMessage {...messages.filesFromCoordinator} />
                    </div>
                    <Button size="xs"
                      onClick={() => window.open(`/download/task/files/${task.job.uid}/source`)}
                    >
                      <span>
                        <Icon icon="download" />
                        <FormattedMessage {...messages.downloadAllFiles} />
                      </span>
                    </Button>
                  </div>
                  <FileList files={task.jobFiles} />
              </div>
            }
            { !!task.versionFiles.length  && 
              <div className="h-auto" >
                <div className="text-md px-4 py-2  bg-gray-100 border-b border-gray-200 font-bold">
                  { versionFilesTitle }
                </div>
                <FileList files={task.versionFiles} />
              </div>
            }
            { ["PR", "NPR"].includes(task?.taskType?.shortname) && !!task.versionTasks.length && 
              <div className="h-auto" >
                <div className="text-md px-4 py-2  bg-gray-100 border-b border-gray-200 font-bold">
                  <FormattedMessage {...messages.versionTasks} />
                </div>
                <DataTable
                  
                  components={{
                    Tr: ({ childProps = {}, ...innerProps }) => {
                      return (
                        <Table.Tr
                          {...innerProps }
                        />
                      )
                    },
                    Td: ({ childProps = {}, ...props }) => (
                      <Table.Td
                        {...props }
                      />
                    )
                  }}
                  head={{
                    uid: { sortable: true, label: 'Task ID' },
                    type: { sortable: true, label: 'Type' },
                    supplier: { sortable: true, label: 'Supplier' },
                    status: { sortable: true, label: 'Status' },
                  }}
                  body={{
                    uid: (entry) => <Span className='text-xs'> {entry.uid} </Span>,
                    type: (entry) => <Span className='text-xs'> {entry.type} </Span>,
                    supplier:  (entry) => <Span className='text-xs space-x-1'>
                      {entry.supplier}
                      {entry.supplierPriorities && !!entry.supplierPriorities.length &&
                        <PriorityIcons priorities={entry.supplierPriorities.map(({slug}) => slug)}/>
                      }
                      </Span>,
                    status: (entry) => <Span className='text-xs'> {entry.status} </Span>,
                  }}
                  entries={task.versionTasks}
                />
              </div>
            }
            { !(!(+task.supplierapproval) && task.declinedreason == '') &&
              <div className="h-auto" >
                { (task.taskType.shortname == 'Ai') && 
                  <AiVoiceLineList refetch={fetchTask} taskId={task.uid} supplierId={task.supplier} aiVoiceLines={aiVoiceLines} />
                }
                { (task.taskType.shortname != 'Ai') && 
                  <FileList refetch={fetchTask} deletable={!(+task.customerapproval)} files={task.taskFiles} />
                }
                <br/>
                { !['LSA', 'LSP', 'USAGE'].includes(task.taskType.shortname) &&
                  <>
                    {showFileAdjustmentGuide && 
                      <div className="px-2">
                        <div className=" border rounded py-3 px-4 shadow-md text-xs text-white bg-primary my-6">
                          <Icon
                            icon="info"
                            className="mr-2"
                          />
                          <Button size="xs"
                            className="font-medium border-b border-dashed"
                            onClick={() => history.push('/docs/File%20Format%20Settings')}
                          >
                            Click here
                          </Button> 
                          <FormattedMessage {...messages.conversionGuide} />
                        </div>
                      </div>
                    }
                    { !!(+task.supplierapproval) && task.declinedreason == '' && task.taskType.shortname !== 'Ai' && !(retakeTasks) &&
                      <>
                        <FileUpload
                          taskId={task.uid}
                          showFileAdjustmentGuide={(val) => setShowFileAdjustmentGuide(val)}
                          onFileAdded={(files) => setTask({...task, taskFiles: files})}
                          refetch={fetchTask}/>
                      </>
                    }
                    { retakeTasks &&
                      <Banner
                        state="warning"
                        title={formatMessage(messages.retakeTitle)}
                        subtitle={retakeTasks.length == 1
                          ? formatMessage(messages.retakeSubTitle, {
                              a: (chunks) => (
                                <a className="underline cursor-pointer" href={`${protocol}//${domain}/task/${retakeTasks[0]?.uid}`} target="_blank">
                                  {chunks}
                                </a>
                              ),
                            })
                          : formatMessage(messages.retakeSubTitleMultiple, {
                              links: retakeTasks.map((retakeTask, index) => 
                                <span key={retakeTask.uid}>
                                  <a className="underline cursor-pointer" href={`${protocol}//${domain}/task/${retakeTask.uid}`} target="_blank">
                                    {retakeTask.uid}
                                  </a>
                                  {index < task.retakeTasks.length - 1 ? ', ' : ''}
                                </span>
                              )
                            })}
                      />
                    }
                  </>
                }
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  )
}

export default SupplierTaskPage
